import { useState, useEffect } from 'react';

export default function ComparePage({ shoppingList = [], onAddToList, favourites = [], onToggleFavourite }) {
  const [pairs,   setPairs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [sortBy,  setSortBy]  = useState('saving');   // saving | woolworths | coles | name
  const [filter,  setFilter]  = useState('all');       // all | woolworths | coles | same

  const listSet = new Set((shoppingList || []).map(i => i.name + i.store));
  const favSet  = new Set((favourites   || []).map(f => f.name + f.store));

  useEffect(() => {
    fetch('/data/compare.json', { cache: 'no-store' })
      .then(r => r.json())
      .then(data => { setPairs(data.matched || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Filter
  let display = pairs;
  if (search.trim()) {
    const q = search.toLowerCase();
    display = display.filter(p =>
      p.woolworths.name.toLowerCase().includes(q) ||
      p.coles.name.toLowerCase().includes(q)
    );
  }
  if (filter !== 'all') {
    display = display.filter(p =>
      filter === 'same' ? p.cheaperAt === 'same' : p.cheaperAt.toLowerCase() === filter
    );
  }

  // Sort
  const sorted = [...display];
  if      (sortBy === 'saving')      sorted.sort((a, b) => b.saving - a.saving);
  else if (sortBy === 'woolworths')  sorted.sort((a, b) => a.woolworths.salePrice - b.woolworths.salePrice);
  else if (sortBy === 'coles')       sorted.sort((a, b) => a.coles.salePrice - b.coles.salePrice);
  else if (sortBy === 'name')        sorted.sort((a, b) => a.woolworths.name.localeCompare(b.woolworths.name));

  const woolCount  = pairs.filter(p => p.cheaperAt === 'Woolworths').length;
  const colesCount = pairs.filter(p => p.cheaperAt === 'Coles').length;
  const sameCount  = pairs.filter(p => p.cheaperAt === 'same').length;

  if (loading) {
    return (
      <div className="compare-loading">
        <span className="spinner" />
        Matching products between Woolworths and Coles…
      </div>
    );
  }

  if (!pairs.length) {
    return (
      <div className="compare-empty-state">
        <p>No deals loaded yet — run a scrape first.</p>
      </div>
    );
  }

  return (
    <div className="compare-page">
      <div className="compare-page-header">
        <h2 className="compare-page-title">Price Comparison</h2>
        <p className="compare-page-sub">{pairs.length} products matched between Woolworths and Coles this week</p>
      </div>

      {/* Summary pills */}
      <div className="compare-summary-row">
        <button
          className={`csr-pill csr-pill--wool ${filter === 'woolworths' ? 'csr-pill--active' : ''}`}
          onClick={() => setFilter(f => f === 'woolworths' ? 'all' : 'woolworths')}
        >
          <span className="csr-store">Woolworths cheaper</span>
          <span className="csr-count">{woolCount}</span>
        </button>
        <button
          className={`csr-pill csr-pill--coles ${filter === 'coles' ? 'csr-pill--active' : ''}`}
          onClick={() => setFilter(f => f === 'coles' ? 'all' : 'coles')}
        >
          <span className="csr-store">Coles cheaper</span>
          <span className="csr-count">{colesCount}</span>
        </button>
        <button
          className={`csr-pill csr-pill--same ${filter === 'same' ? 'csr-pill--active' : ''}`}
          onClick={() => setFilter(f => f === 'same' ? 'all' : 'same')}
        >
          <span className="csr-store">Same price</span>
          <span className="csr-count">{sameCount}</span>
        </button>
      </div>

      {/* Controls */}
      <div className="compare-controls">
        <input
          className="compare-search"
          placeholder="Search products…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="compare-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="saving">Biggest saving</option>
          <option value="woolworths">Lowest Woolworths price</option>
          <option value="coles">Lowest Coles price</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      {/* Column headers */}
      <div className="compare-col-headers">
        <span className="cch-wool">Woolworths</span>
        <span className="cch-center" />
        <span className="cch-coles">Coles</span>
      </div>

      {/* Pairs */}
      <div className="compare-list">
        {sorted.map((pair, i) => {
          const woolWins  = pair.cheaperAt === 'Woolworths';
          const colesWins = pair.cheaperAt === 'Coles';
          const woolInList  = listSet.has(pair.woolworths.name + 'Woolworths');
          const colesInList = listSet.has(pair.coles.name + 'Coles');
          const woolFav     = favSet.has(pair.woolworths.name + 'Woolworths');
          const colesFav    = favSet.has(pair.coles.name + 'Coles');

          // Use the shorter/cleaner name for the center label
          const displayName = pair.woolworths.name.length <= pair.coles.name.length
            ? pair.woolworths.name
            : pair.coles.name;

          return (
            <div key={i} className={`compare-row ${woolWins ? 'compare-row--wool-wins' : colesWins ? 'compare-row--coles-wins' : ''}`}>

              {/* Woolworths column */}
              <div className={`compare-col compare-col--wool ${woolWins ? 'compare-col--winner' : ''}`}>
                {pair.woolworths.image && (
                  <img src={pair.woolworths.image} alt="" className="compare-img" loading="lazy" />
                )}
                <div className="compare-prices">
                  <span className="compare-sale-price">${pair.woolworths.salePrice.toFixed(2)}</span>
                  {pair.woolworths.originalPrice && (
                    <span className="compare-orig-price">${pair.woolworths.originalPrice.toFixed(2)}</span>
                  )}
                  {pair.woolworths.discount && (
                    <span className="compare-off-badge">{pair.woolworths.discount}% off</span>
                  )}
                </div>
                <div className="compare-col-actions">
                  {onToggleFavourite && (
                    <button
                      className={`compare-fav-btn ${woolFav ? 'compare-fav-btn--on' : ''}`}
                      onClick={() => onToggleFavourite(pair.woolworths)}
                      aria-label={woolFav ? 'Remove from favourites' : 'Save to favourites'}
                    >{woolFav ? '♥' : '♡'}</button>
                  )}
                  <button
                    className={`compare-add-btn ${woolInList ? 'compare-add-btn--in' : ''}`}
                    onClick={() => !woolInList && onAddToList(pair.woolworths)}
                    disabled={woolInList}
                  >{woolInList ? '✓ In list' : '+ Add'}</button>
                </div>
              </div>

              {/* Centre: product name + badge */}
              <div className="compare-center">
                <span className="compare-product-name">{displayName}</span>
                {pair.cheaperAt !== 'same' ? (
                  <span className={`compare-cheaper-badge compare-cheaper-badge--${pair.cheaperAt.toLowerCase()}`}>
                    {pair.cheaperAt} saves ${pair.saving.toFixed(2)}
                  </span>
                ) : (
                  <span className="compare-cheaper-badge compare-cheaper-badge--same">Same price</span>
                )}
                {pair.woolworths.url && (
                  <a href={pair.woolworths.url} target="_blank" rel="noreferrer" className="compare-view-link compare-view-link--wool">W ↗</a>
                )}
                {pair.coles.url && (
                  <a href={pair.coles.url} target="_blank" rel="noreferrer" className="compare-view-link compare-view-link--coles">C ↗</a>
                )}
              </div>

              {/* Coles column */}
              <div className={`compare-col compare-col--coles ${colesWins ? 'compare-col--winner' : ''}`}>
                {pair.coles.image && (
                  <img src={pair.coles.image} alt="" className="compare-img" loading="lazy" />
                )}
                <div className="compare-prices">
                  <span className="compare-sale-price">${pair.coles.salePrice.toFixed(2)}</span>
                  {pair.coles.originalPrice && (
                    <span className="compare-orig-price">${pair.coles.originalPrice.toFixed(2)}</span>
                  )}
                  {pair.coles.discount && (
                    <span className="compare-off-badge">{pair.coles.discount}% off</span>
                  )}
                </div>
                <div className="compare-col-actions">
                  {onToggleFavourite && (
                    <button
                      className={`compare-fav-btn ${colesFav ? 'compare-fav-btn--on' : ''}`}
                      onClick={() => onToggleFavourite(pair.coles)}
                      aria-label={colesFav ? 'Remove from favourites' : 'Save to favourites'}
                    >{colesFav ? '♥' : '♡'}</button>
                  )}
                  <button
                    className={`compare-add-btn ${colesInList ? 'compare-add-btn--in' : ''}`}
                    onClick={() => !colesInList && onAddToList(pair.coles)}
                    disabled={colesInList}
                  >{colesInList ? '✓ In list' : '+ Add'}</button>
                </div>
              </div>

            </div>
          );
        })}
      </div>

      {sorted.length === 0 && (
        <div className="compare-no-results">No products match your search.</div>
      )}
    </div>
  );
}
